import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { CartItem } from '@/types/store'
import type { Product, ProductSize } from '@/types'
import { getSizeVariant, variantImage } from '@/lib/productVariant'

interface CartState {
  items: CartItem[]
  buyNowItem: CartItem | null
  couponCode: string
  discount: number
  giftPackaging: boolean
  addItem: (product: Product, size?: ProductSize, qty?: number) => void
  startBuyNow: (product: Product, size?: ProductSize, qty?: number) => void
  clearBuyNow: () => void
  removeItem: (productId: string, size?: ProductSize) => void
  updateQty: (productId: string, size: ProductSize | undefined, qty: number) => void
  setCoupon: (code: string, discount: number) => void
  clearCoupon: () => void
  setGiftPackaging: (v: boolean) => void
  clearCart: () => void
  subtotal: () => number
  itemCount: () => number
}

function itemKey(productId: string, size?: ProductSize) {
  return `${productId}-${size || 'default'}`
}

function getPrice(product: Product, size?: ProductSize): number {
  if (size && product.sizes?.length) {
    const v = product.sizes.find((s) => s.size === size)
    if (v) return v.discountPrice ?? v.price
  }
  return product.discountPrice ?? product.price
}

function getStock(product: Product, size?: ProductSize): number {
  if (size && product.sizes?.length) {
    return product.sizes.find((s) => s.size === size)?.quantity ?? 0
  }
  return product.quantity
}

function buildCartItem(
  product: Product,
  size: ProductSize | undefined,
  qty: number
): CartItem | null {
  const maxStock = getStock(product, size)
  if (maxStock <= 0) return null
  const variant = size ? getSizeVariant(product, size) : undefined
  const image = variantImage(variant, product)
  return {
    productId: product.id,
    name: product.name,
    size,
    price: getPrice(product, size),
    quantity: Math.min(qty, maxStock),
    ...(image ? { image } : {}),
    sku: variant?.sku ?? product.sku,
    maxStock,
  }
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      buyNowItem: null,
      couponCode: '',
      discount: 0,
      giftPackaging: false,

      addItem: (product, size, qty = 1) => {
        const item = buildCartItem(product, size, qty)
        if (!item) return

        set((state) => {
          const key = itemKey(product.id, size)
          const existing = state.items.find(
            (i) => itemKey(i.productId, i.size) === key
          )
          if (existing) {
            const newQty = Math.min(existing.quantity + qty, item.maxStock)
            return {
              items: state.items.map((i) =>
                itemKey(i.productId, i.size) === key
                  ? { ...i, quantity: newQty }
                  : i
              ),
            }
          }
          return {
            items: [...state.items, item],
          }
        })
      },

      startBuyNow: (product, size, qty = 1) => {
        const item = buildCartItem(product, size, qty)
        if (!item) return
        set({ buyNowItem: item })
      },

      clearBuyNow: () => set({ buyNowItem: null }),

      removeItem: (productId, size) => {
        const key = itemKey(productId, size)
        set((state) => ({
          items: state.items.filter(
            (i) => itemKey(i.productId, i.size) !== key
          ),
        }))
      },

      updateQty: (productId, size, qty) => {
        const key = itemKey(productId, size)
        if (qty <= 0) {
          get().removeItem(productId, size)
          return
        }
        set((state) => ({
          items: state.items.map((i) =>
            itemKey(i.productId, i.size) === key
              ? { ...i, quantity: Math.min(qty, i.maxStock) }
              : i
          ),
        }))
      },

      setCoupon: (code, discount) => set({ couponCode: code, discount }),
      clearCoupon: () => set({ couponCode: '', discount: 0 }),
      setGiftPackaging: (giftPackaging) => set({ giftPackaging }),
      clearCart: () =>
        set({ items: [], couponCode: '', discount: 0, giftPackaging: false }),

      subtotal: () =>
        get().items.reduce((s, i) => s + i.price * i.quantity, 0),

      itemCount: () =>
        get().items.reduce((s, i) => s + i.quantity, 0),
    }),
    {
      name: 'bazm-cart',
      partialize: (state) => ({
        items: state.items,
        couponCode: state.couponCode,
        discount: state.discount,
        giftPackaging: state.giftPackaging,
      }),
    }
  )
)
